import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Users, MapPin, Clock, DollarSign, Briefcase, GraduationCap, Heart, Zap, Globe, Shield, Award, ArrowRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const openPositions = [
  {
    id: 1,
    title: 'Senior Logistics Operations Manager',
    department: 'Operations',
    location: 'San Francisco, CA',
    type: 'Full-time',
    experience: '5+ years',
    salary: '$120K - $150K',
    description: 'Lead CROSSDOCS operations and optimize supply chain processes across multiple facilities.',
    requirements: [
      'Bachelor\'s degree in Supply Chain Management or related field',
      '5+ years experience in logistics operations',
      'Strong leadership and team management skills',
      'Experience with WMS and TMS systems'
    ],
    image: 'assets/warehouse-8921538_1280.jpg'
  },
  {
    id: 2,
    title: 'Software Engineer - Logistics Platform',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    experience: '3+ years',
    salary: '$100K - $130K',
    description: 'Build and maintain our proprietary logistics management platform using modern technologies.',
    requirements: [
      'Bachelor\'s degree in Computer Science or related field',
      '3+ years experience with React, Node.js, and Python',
      'Experience with logistics or supply chain systems',
      'Strong problem-solving and analytical skills'
    ],
    image: 'assets/laptop-3196481_1280.jpg'
  },
  {
    id: 3,
    title: 'Supply Chain Analyst',
    department: 'Analytics',
    location: 'Chicago, IL',
    type: 'Full-time',
    experience: '2+ years',
    salary: '$70K - $90K',
    description: 'Analyze supply chain data to identify optimization opportunities and improve efficiency.',
    requirements: [
      'Bachelor\'s degree in Business, Analytics, or related field',
      '2+ years experience in supply chain analysis',
      'Proficiency in SQL, Excel, and data visualization tools',
      'Strong analytical and communication skills'
    ],
    image: 'assets/truck-1910871_1280.jpg'
  },
  {
    id: 4,
    title: 'Customer Success Manager',
    department: 'Customer Success',
    location: 'New York, NY',
    type: 'Full-time',
    experience: '3+ years',
    salary: '$80K - $100K',
    description: 'Build and maintain relationships with key clients, ensuring their success with our services.',
    requirements: [
      'Bachelor\'s degree in Business or related field',
      '3+ years experience in customer success or account management',
      'Experience in logistics or supply chain industry',
      'Excellent communication and relationship-building skills'
    ],
    image: 'assets/man-5710164_1280.jpg'
  }
];

const benefits = [
  {
    icon: Heart,
    title: 'Health & Wellness',
    description: 'Comprehensive health, dental, and vision coverage for you and your family'
  },
  {
    icon: DollarSign,
    title: 'Competitive Compensation',
    description: 'Above-market salaries with equity options and performance bonuses'
  },
  {
    icon: Clock,
    title: 'Flexible Work',
    description: 'Remote work options and flexible hours to support work-life balance'
  },
  {
    icon: GraduationCap,
    title: 'Learning & Development',
    description: 'Professional development budget and opportunities for growth'
  },
  {
    icon: Globe,
    title: 'Global Impact',
    description: 'Work on solutions that impact global supply chains and commerce'
  },
  {
    icon: Award,
    title: 'Recognition',
    description: 'Regular recognition programs and career advancement opportunities'
  }
];

const values = [
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We believe in the power of diverse teams working together'
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'Constantly pushing boundaries to solve complex logistics challenges'
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'Building trust through transparency and ethical business practices'
  }
];

export default function Careers() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Careers — CROSSDOCS</title>
        <meta name="description" content="Join the CROSSDOCS team and help revolutionize global logistics. Explore career opportunities in operations, engineering, analytics, and more." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Join Our Mission to
              <span className="block text-primary-400">Revolutionize Logistics</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Help us build the future of supply chain optimization. Join a team of passionate 
              professionals dedicated to solving complex logistics challenges.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="#open-positions" 
                className="inline-flex items-center rounded-xl bg-primary-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all duration-300"
              >
                View Open Positions
              </a>
              <NavLink 
                to="/about" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-lg font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Learn About Us
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Why Work at CROSSDOCS?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We're building more than just a logistics company – we're creating a movement 
              to transform how goods move around the world.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
              </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Our Values
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              These core principles guide everything we do and shape our company culture.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="h-8 w-8 text-white" />
            </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{value.title}</h3>
                <p className="text-slate-600 text-lg">{value.description}</p>
              </motion.div>
          ))}
        </div>
      </div>
      </section>

      {/* Open Positions */}
      <section id="open-positions" className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Open Positions
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Ready to join our team? Explore current opportunities and find your perfect fit.
            </p>
          </motion.div>

          <div className="space-y-8">
            {openPositions.map((position, index) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200"
              >
                <div className="grid lg:grid-cols-3 gap-8 p-8">
                  {/* Position Image */}
                  <div className="lg:col-span-1">
                    <img
                      src={position.image}
                      alt={position.title}
                      className="w-full h-48 lg:h-full object-cover rounded-xl"
                    />
                  </div>

                  {/* Position Details */}
                  <div className="lg:col-span-2">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
                        {position.department}
                      </span>
                      <div className="flex items-center gap-2 text-slate-500">
                        <MapPin className="h-4 w-4" />
                        {position.location}
                      </div>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Briefcase className="h-4 w-4" />
                        {position.type}
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{position.title}</h3>
                    <p className="text-slate-600 mb-4">{position.description}</p>

                    <div className="grid sm:grid-cols-3 gap-4 mb-6">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{position.experience}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{position.salary}</span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold text-slate-900 mb-3">Requirements:</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, reqIndex) => (
                          <li key={reqIndex} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-slate-600">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300">
                      Apply Now
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* No Match Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-16 p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Don't See a Perfect Match?
            </h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              We're always looking for talented individuals to join our team. Send us your resume 
              and let us know how you can contribute to our mission.
            </p>
            <button className="inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300">
              Submit General Application
              <ArrowRight className="h-5 w-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container-consistent text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Make an Impact?
            </h2>
            <p className="text-lg text-primary-100 max-w-3xl mx-auto mb-8">
              Join a team that's passionate about solving complex logistics challenges and 
              building the future of supply chain optimization.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="#open-positions" 
                className="inline-flex items-center rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                View All Positions
              </a>
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-base font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Get in Touch
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






